import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	Button, Empty, Form, Input, Popconfirm, Popover, Row, Table, Tabs
} from 'antd'
import {
	CrownOutlined, DeleteOutlined, EuroCircleOutlined, HomeOutlined, PhoneOutlined, PlusCircleOutlined, QuestionCircleOutlined, SettingOutlined, TeamOutlined, UserOutlined
} from '@ant-design/icons'
import i18next from 'i18next'
import { debounce, get } from 'lodash'
import { useTranslation } from 'react-i18next'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import {
	BooleanParam, NumberParam, StringParam, useQueryParams, withDefault
} from 'use-query-params'
import { history } from '../../utils/history'
import { deleteUser, getUsers, UsersTableItem } from '../../reducers/usersU/actions'
import { PAGINATION, USER_ROLE } from '../../utils/enums'
import { RootState } from '../../reducers'
import PayslipDrawer from './components/PayslipDrawer'

type Columns = ColumnsType<UsersTableItem>

const Users = () => {
	const dispatch = useDispatch()
	const { t } = useTranslation('pages')
	const [filter, setFilter] = useState<any | null>({})

	// State for Payslip Drawer
	// const [userIDPayslipDrawer, setuserIDPayslipDrawer] = useState<number | null>(null)
	// const [openPayslipDrawer, setOpenPayslipDrawer] = useState<boolean>(false)

	const [query, setQuery] = useQueryParams({
		openPayslipDrawer: withDefault(BooleanParam, false),
		userIDPayslipDrawer: withDefault(NumberParam, null),
		role: withDefault(StringParam, 'ALL')
	})

	const ROLE_TABS = [
		{ key: 'ALL', label: 'Všetci', icon: <TeamOutlined /> },
		{ key: USER_ROLE.USER, label: 'Zamestnanci', icon: <UserOutlined /> },
		{ key: USER_ROLE.JOB_TRUSTEE, label: 'Domovníci', icon: <HomeOutlined /> },
		{ key: USER_ROLE.PHONE_OPERATOR, label: 'Telefonistky', icon: <PhoneOutlined /> },
		{ key: USER_ROLE.ADMIN, label: 'Admin', icon: <SettingOutlined /> },
		{ key: USER_ROLE.SUPERADMIN, label: 'Superadmin', icon: <CrownOutlined /> }
	]

	const columns: Columns = [
		{
			title: i18next.t('pages:Priezvisko'),
			dataIndex: 'lastName',
			key: 'lastName',
			sorter: true,
			ellipsis: true,
			render: (value) => (value || '-')
		},
		{
			title: i18next.t('pages:Meno'),
			dataIndex: 'firstName',
			key: 'firstName',
			sorter: true,
			ellipsis: true,
			render: (value) => (value || '-')
		},
		{
			title: i18next.t('pages:Telefónne číslo'),
			dataIndex: 'phone',
			key: 'phone',
			ellipsis: true,
			render: (value) => (value || '-')
		},
		{
			title: i18next.t('pages:Kontaktný mail'),
			dataIndex: 'email',
			key: 'email',
			ellipsis: true,
			render: (value) => (value || '-')
		},
		{
			title: '',
			key: 'operation',
			fixed: 'right',
			width: 100,
			render(text, record) {
				return (
					<Row>
						<Popover content={i18next.t('pages:Výplatná páska')}>
							<Button
								className={'mr-2'}
								icon={ <EuroCircleOutlined /> }
								type={ 'primary' }
								onClick={ (e) => {
									setQuery({
										...query,
										userIDPayslipDrawer: record.id,
										openPayslipDrawer: true
									})
									e.stopPropagation()
								} }
							/>
						</Popover>
						<Popconfirm
							title={ i18next.t('pages:Skutočne chcete vymazať záznam?') }
							icon={ <QuestionCircleOutlined style={ { color: 'red' } } /> }
							cancelText={ i18next.t('pages:Zrušiť') }
							okText={ i18next.t('pages:Vymazať') }
							onConfirm={ (e) => {
								e?.stopPropagation()
								dispatch(deleteUser(record.id, () => {
									dispatch(getUsers(1, PAGINATION.defaultPageSize, { ...filter, page: 1 }))
								}))
							} }
							onCancel={ (e) => e?.stopPropagation() }
							okButtonProps={ {
								size: 'small',
								type: 'primary'
							} }
							cancelButtonProps={ {
								size: 'small',
								type: 'ghost'
							} }
						>
							<Button
								icon={ <DeleteOutlined /> }
								type={ 'primary' }
								onClick={ (e) => e.stopPropagation() }
							/>
						</Popconfirm>
					</Row>
				)
			}
		}
	]

	const users = useSelector((state: RootState) => state.users.users)
	const user = useSelector((state: RootState) => state.users.detail)

	useEffect(() => {
		const roleFilter = query.role === 'ALL' ? undefined : [query.role]
		dispatch(getUsers(1,
			PAGINATION.defaultPageSize,
			{ ...filter, role: roleFilter }))
	}, [filter, query.role])

	const handleTableChange = (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<UsersTableItem> | SorterResult<UsersTableItem>[], extra: TableCurrentDataSource<UsersTableItem>) => {
		let order = {}
		if ((sorter as SorterResult<any>)?.order) {
			const sorterLocal = (sorter as SorterResult<any>)
			order = {
				orderBy: sorterLocal.field,
				orderDirection: sorterLocal.order === 'ascend' ? 'asc' : 'desc'
			}
		}
		setFilter({
			...filter,
			page: pagination.current,
			...order
		})
	}

	const debounced = useCallback(debounce((searchTerm: string) => setFilter({ ...filter, search: searchTerm, page: 1 }), 300), [filter])

	const handleOnChange = ((e: React.ChangeEvent<HTMLInputElement>) => {
		debounced(e.target.value)
	})

	return (
		<div className={ 'page-wrapper' }>
			<div className={ 'flex justify-end' }>
				<Button
					icon={ <PlusCircleOutlined /> }
					href={ t('paths:userCreate|path') }
					type={ 'primary' }
					className={'mt-6'}
				>
					{ t('pages:Pridať Používateľa') }
				</Button>
			</div>
			<div className={ 'flex justify-between' } style={{ marginTop: 8 }}>
				<Form.Item>
					<Input.Search onChange={ handleOnChange } style={ { width: 300 } } allowClear />
				</Form.Item>
			</div>
			<Tabs
				activeKey={query.role ?? USER_ROLE.USER}
				onChange={(key) => {
					setQuery({ ...query, role: key })
					setFilter({ ...filter, page: 1 })
				}}
				style={{ marginBottom: 8, marginTop: -8 }}
			>
				{ROLE_TABS.map((tab) => (
					<Tabs.TabPane
						key={tab.key}
						tab={
							<span>
								{tab.icon}
								<span style={{ marginLeft: 6 }}>{tab.label}</span>
							</span>
						}
					/>
				))}
			</Tabs>
			<Table
				className={ 'general-table' }
				columns={ columns }
				dataSource={ users.tableData }
				onChange={ handleTableChange }
				showSorterTooltip={ false }
				rowKey={ 'id' }
				style={ { marginTop: -20 } }
				pagination={ {
					pageSize: PAGINATION.defaultPageSize,
					total: users.pagination?.totalCount,
					current: users.pagination?.page,
					showSizeChanger: false
				} }
				loading={ user.isLoading || users.isLoading }
				onRow={ (record) => ({
					onClick: () => history.push(`${t('paths:userDetail|path')}/${get(record, 'id')}`)
				}) }
				locale={ {
					emptyText: (
						<Empty description={ t('pages:Žiadne dáta') } />
					)
				} }
				size={ 'small' }
			/>
			{query.userIDPayslipDrawer
				&& <PayslipDrawer
					userID={query.userIDPayslipDrawer}
					onClose={ () => setQuery({
						...query,
						openPayslipDrawer: false
					})}
					open={query.openPayslipDrawer}
					onRefreshList={ () => {} }
				/>}
		</div>
	)
}

Users.propTypes = {}

export default Users
